/**
 * Core-100 R2 — CORE-BD-GAP-004/005/006: the nineteen questions Core exists to answer, together,
 * with the partial ones LABELLED as partial (and why), and the shop's measured outcome from durable
 * records (`core_outcome_summary`). A figure with no sample says so; it is never shown as 0% or 100%.
 */
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

/**
 * Core-100 R4 — CORE-BD responsibilities 1 and 7: the nineteen answers are the SHOP'S, derived on read by the server
 * (`core_question_answers`, owner/manager) — no longer a constant that read "full" for Q1/Q15 in a shop whose
 * technicians have no hours, or for Q19 in a shop OptiBay has learned nothing from. A structural partial is the
 * product's limit; a data partial is this shop's, and states how many records it rests on.
 */
export interface CoreQuestion {
  n: number;
  question: string;
  answer: 'full' | 'partial';
  basis: 'structural' | 'data';
  why: string | null;
  evidence: number | null;
  source: string;
}

export function describeQuestion(q: CoreQuestion): string {
  if (q.answer === 'full') return q.question;
  return `${q.question} — ${q.why ?? 'partly answered'}${q.basis === 'data' ? ' (for this shop)' : ''}`;
}

export interface OutcomeRow { metric: string; value: number | null; sample: number; note: string }

const METRIC_LABEL: Record<string, string> = {
  promises_kept: 'Promises in trouble that were kept',
  carries_with_reason: 'Carried-over work with a stated or derived reason',
  run_long_explained: 'Run-long explained by added work',
  integrity_latest: 'Latest independent integrity check',
  suggestions_accepted: 'Schedule suggestions accepted',
};

export function describeOutcome(r: OutcomeRow): string {
  const label = METRIC_LABEL[r.metric] ?? r.metric;
  if (r.value === null) return `${label}: ${r.note}`;
  if (r.metric === 'integrity_latest') return `${label}: ${r.value === 1 ? 'passed' : 'found contradictions'}`;
  return `${label}: ${Math.round(r.value * 100)}% (of ${r.sample})`;
}

export function CoreOutcomeCard({ shopId }: { shopId: string }) {
  const q = useQuery({
    queryKey: ['core-outcome', shopId],
    queryFn: async (): Promise<OutcomeRow[]> => {
      const { data, error } = await supabase.rpc('core_outcome_summary' as never, { p_shop_id: shopId, p_days: 30 } as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as OutcomeRow[];
    },
  });
  const answers = useQuery({
    queryKey: ['core-question-answers', shopId],
    queryFn: async (): Promise<CoreQuestion[]> => {
      const { data, error } = await supabase.rpc('core_question_answers' as never, { p_shop_id: shopId } as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as CoreQuestion[];
    },
  });
  const all = answers.data ?? [];
  const partial = all.filter((c) => c.answer === 'partial');
  return (
    <Card data-testid="core-outcome">
      <CardHeader>
        <CardTitle className="text-base">What OptiBay Core answers for this shop</CardTitle>
        <CardDescription data-testid="core-questions-summary">
          {answers.isError
            ? 'Which questions are answered for this shop could not be read just now.'
            : answers.data
              ? `${all.length - partial.length} of ${all.length} questions are answered in full for this shop; the rest are labelled with why.`
              : 'Reading which questions are answered for this shop…'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <ul className="space-y-1" data-testid="core-questions-partial">
          {partial.map((c) => (
            <li key={c.n} data-testid={`core-question-${c.n}`} data-basis={c.basis}>
              <Badge variant="secondary" className="mr-2">partial</Badge>{describeQuestion(c)}
            </li>
          ))}
        </ul>
        <div>
          <p className="text-xs font-medium text-muted-foreground">Outcome, last 30 days</p>
          {q.isError && <p className="text-muted-foreground">The outcome could not be read.</p>}
          <ul className="space-y-1" data-testid="core-outcome-metrics">
            {q.data?.map((r) => <li key={r.metric}>{describeOutcome(r)}</li>)}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
